import { Persona } from 'interfaces/db/persona'
import { OutlineSchema } from 'schemas/outlineSchema'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

export type TemplateFormProps = {
  mainPrompt: string
  seoKeywords: string[]
  persona: Persona | null
  selectedEpisodes: Array<{ value: string; label: string }>
  template: any
  tone: string
  language: string
  submissionIds: string[]
  selectAllEpisodes: boolean
}

type TemplateFormState = {
  formStep: 'outline' | 'review' | 'write'
  generatedContentStep: 'outline' | 'review' | 'write'
  formData: TemplateFormProps
  outline: any
  generatedContent: string
  generatedContentDbId: number | null
  isLoadingOutline: boolean
  isLoadingContent: boolean
  error: any
  setFormStep: (step: 'outline' | 'review' | 'write') => void
  setGeneratedContentStep: (step: 'outline' | 'review' | 'write') => void
  setFormData: (data: Partial<TemplateFormProps>) => void
  setOutline: (outline: any) => void
  setGeneratedContent: (content: string) => void
  setGeneratedContentDbId: (id: number) => void
  setIsLoadingOutline: (loading: boolean) => void
  setIsLoadingContent: (loading: boolean) => void
  setError: (error: any) => void
  resetGeneratedContent: () => void
  resetGeneratedContentDbId: () => void
  resetOutline: () => void
  resetFormData: () => void
  updateEntireStateFromDbItem: (dbItem: any) => void
  updateSeoKeywords: (keywords: string[]) => void
}

const initialFormData: TemplateFormProps = {
  mainPrompt: '',
  seoKeywords: [],
  persona: null,
  selectedEpisodes: [],
  template: null,
  tone: '',
  language: 'English',
  submissionIds: [],
  selectAllEpisodes: false,
}

export const useTemplateFormStore = create<TemplateFormState>((set) => ({
  formStep: 'outline',
  generatedContentStep: 'outline',
  formData: initialFormData,
  outline: null,
  generatedContent: '',
  generatedContentDbId: null,
  isLoadingOutline: false,
  isLoadingContent: false,
  error: null,
  setFormStep: (step) => set({ formStep: step }),
  setGeneratedContentStep: (step) => set({ generatedContentStep: step }),
  setFormData: (data) =>
    set((state) => ({ formData: { ...state.formData, ...data } })),
  setOutline: (outline) => set({ outline }),
  setGeneratedContent: (content) => set({ generatedContent: content }),
  setGeneratedContentDbId: (id) => set({ generatedContentDbId: id }),
  setIsLoadingOutline: (loading) => set({ isLoadingOutline: loading }),
  setIsLoadingContent: (loading) => set({ isLoadingContent: loading }),
  setError: (error) => set({ error }),
  resetGeneratedContent: () => set({ generatedContent: '' }),
  resetGeneratedContentDbId: () => set({ generatedContentDbId: null }),
  resetOutline: () => set({ outline: null }),
  resetFormData: () => set({ formData: initialFormData }),
  updateSeoKeywords: (keywords) =>
    set((state) => ({
      formData: { ...state.formData, seoKeywords: keywords },
    })),
  updateEntireStateFromDbItem: (dbItem) =>
    set((state) => ({
      generatedContent: dbItem.content || '',
      outline: dbItem.outline || null,
      generatedContentDbId: dbItem.id || null,
      formData: {
        ...state.formData,
        template: dbItem.template || null,
        mainPrompt: dbItem.main_prompt || '',
        seoKeywords: dbItem.seo_keywords || [],
        persona: dbItem.persona || null,
        tone: dbItem.tone || '',
        language: dbItem.language || 'English',
        submissionIds: dbItem.submission_ids || [],
        selectAllEpisodes: dbItem.select_all_episodes || false,
      },
    })),
}))
